import React, { useCallback, useEffect, useState, ReactNode } from 'react';
import {
  HomePageTopVisited,
  HomePageRecentlyVisited,
  HomePageStarredEntities,
  HomePageToolkit,
  VisitDisplayProvider,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import {
  Box,
  Chip,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import LanguageIcon from '@material-ui/icons/Language';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import BuildIcon from '@material-ui/icons/Build';
import CodeIcon from '@material-ui/icons/Code';
import CategoryIcon from '@material-ui/icons/Category';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { PORTAL_BADGE_EVENT } from '../settings/PortalBadgeSettings';
import { AnnouncementsCard } from '@backstage-community/plugin-announcements';

/* ── Error Boundary for Announcements Card ──────────────────────────── */
class AnnouncementErrorBoundary extends React.Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any) {
    console.error('AnnouncementsCard error:', error);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/* ── SourceFuse brand tokens ─────────────────────────────────────────── */
const SF = {
  red: '#E81823',
  navy: '#212D38',
  darkBlue: '#060C3A',
  bodyText: '#525252',
  lightBg: '#F7F8F9',
  white: '#FFFFFF',
  border: '#E4E4E4',
  cardShadow: '0 2px 16px rgba(33, 45, 56, 0.08)',
};

const useStyles = makeStyles(() => ({
  /* Full-page wrapper – covers the dark Backstage page-theme gradient */
  root: {
    backgroundColor: SF.lightBg,
    minHeight: '100vh',
    /* bleed past the 24px padding that Content adds */
    margin: '-24px -24px 0',
    padding: 0,
    overflowX: 'hidden',
  },

  /* ── Hero banner ─────────────────────────────────────────────────── */
  hero: {
    background: `linear-gradient(135deg, ${SF.navy} 0%, ${SF.darkBlue} 100%)`,
    padding: '56px 40px 68px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  /* decorative glow circles */
  heroDeco1: {
    position: 'absolute',
    top: '-80px',
    right: '-80px',
    width: '360px',
    height: '360px',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${SF.red}33 0%, transparent 65%)`,
    pointerEvents: 'none',
  },
  heroDeco2: {
    position: 'absolute',
    bottom: '-100px',
    left: '4%',
    width: '280px',
    height: '280px',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${SF.red}22 0%, transparent 65%)`,
    pointerEvents: 'none',
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '680px',
    margin: '0 auto',
  },
  heroBadge: {
    display: 'inline-block',
    backgroundColor: `${SF.red}22`,
    color: '#FF6B78',
    border: `1px solid ${SF.red}55`,
    borderRadius: '20px',
    padding: '4px 18px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.2px',
    textTransform: 'uppercase' as const,
    marginBottom: '20px',
    fontFamily: 'Gotham, sans-serif',
  },
  heroTitle: {
    color: SF.white,
    fontSize: '2.6rem',
    fontWeight: 700,
    lineHeight: 1.18,
    marginBottom: '14px',
    fontFamily: 'Gotham, sans-serif',
  },
  heroRed: {
    color: SF.red,
  },
  heroSubtitle: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: '1rem',
    lineHeight: 1.65,
    marginBottom: '40px',
    fontFamily: 'Gotham, sans-serif',
  },

  /* ── Search bar inside hero ──────────────────────────────────────── */
  searchWrapper: {
    maxWidth: '580px',
    margin: '0 auto',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 6px 28px rgba(0,0,0,0.35)',
    '& .MuiPaper-root': {
      borderRadius: '8px !important',
      boxShadow: 'none !important',
      backgroundColor: `${SF.white} !important`,
    },
    '& .MuiInputBase-root': {
      borderRadius: '8px',
      backgroundColor: `${SF.white} !important`,
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: `${SF.white} !important`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& input': {
      backgroundColor: `${SF.white} !important`,
      color: '#333333 !important',
    },
    '& .MuiSvgIcon-root': {
      color: '#999999 !important',
    },
    /* CLEAR button */
    '& .MuiButton-root, & button': {
      color: `${SF.navy} !important`,
    },
  },

  /* ── Thin accent divider ─────────────────────────────────────────── */
  accentStrip: {
    height: '4px',
    background: `linear-gradient(90deg, ${SF.red} 0%, ${SF.navy} 55%, ${SF.lightBg} 100%)`,
  },

  /* ── Main content area ───────────────────────────────────────────── */
  mainContent: {
    padding: '36px 32px 56px',
    maxWidth: '1400px',
    margin: '0 auto',
  },

  /* ── Card wrapper ────────────────────────────────────────────────── */
  cardWrapper: {
    backgroundColor: SF.white,
    borderRadius: '10px',
    boxShadow: SF.cardShadow,
    border: `1px solid ${SF.border}`,
    overflow: 'hidden',
    height: '100%',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(33, 45, 56, 0.14)',
    },
    /* Force white backgrounds on ALL inner MUI card elements */
    '& .MuiCard-root': {
      backgroundColor: `${SF.white} !important`,
      backgroundImage: 'none !important',
      boxShadow: 'none !important',
      borderRadius: 0,
    },
    '& .MuiCardContent-root': {
      backgroundColor: `${SF.white} !important`,
    },
    '& .MuiCardHeader-root': {
      backgroundColor: `${SF.white} !important`,
      backgroundImage: 'none !important',
      borderBottom: `2px solid ${SF.red}`,
      paddingBottom: '12px',
    },
    '& .MuiCardHeader-title': {
      color: `${SF.navy} !important`,
      fontWeight: '700 !important',
      fontSize: '1rem !important',
      fontFamily: 'Gotham, sans-serif !important',
    },
    '& .MuiPaper-root': {
      backgroundColor: `${SF.white} !important`,
    },
    /* Dividers */
    '& .MuiDivider-root': {
      backgroundColor: `${SF.border}`,
    },
    /* Link colours: navy default, red on hover */
    '& a': {
      color: `${SF.navy}`,
      textDecoration: 'none',
    },
    '& a:hover': {
      color: `${SF.red}`,
      textDecoration: 'underline',
    },
    /* Chips / badges */
    '& .MuiChip-root': {
      backgroundColor: `${SF.navy} !important`,
      color: `${SF.white} !important`,
    },
    /* Toolkit icon circles — SF navy */
    '& .MuiListItemIcon-root': {
      backgroundColor: `${SF.navy} !important`,
      color: `${SF.white} !important`,
    },
    '& .MuiListItemIcon-root svg': {
      color: `${SF.white} !important`,
      fill: `${SF.white} !important`,
    },
    /* VIEW MORE button */
    '& .MuiButton-root': {
      color: `${SF.red} !important`,
      fontWeight: 600,
    },
    /* Secondary text (count, time) */
    '& .MuiTypography-colorTextSecondary': {
      color: `${SF.bodyText} !important`,
    },
  },
}));

/* ── Toolkit quick-links ─────────────────────────────────────────────── */
const toolkitTools = [
  {
    url: 'https://backstage.io/docs',
    label: 'Docs',
    icon: <MenuBookIcon />,
  },
  {
    url: 'https://github.com/sourcefuse',
    label: 'GitHub',
    icon: <GitHubIcon />,
  },
  {
    url: 'https://sourcefuse.com',
    label: 'SourceFuse',
    icon: <LanguageIcon />,
  },
  {
    url: '/tech-radar',
    label: 'Tech Radar',
    icon: <MyLocationIcon />,
  },
  {
    url: '/announcements',
    label: 'Announcements',
    icon: <AnnouncementIcon />,
  },
  {
    url: '/create',
    label: 'Create',
    icon: <BuildIcon />,
  },
  {
    url: '/api-docs',
    label: 'API Docs',
    icon: <CodeIcon />,
  },
  {
    url: '/catalog',
    label: 'Catalog',
    icon: <CategoryIcon />,
  },
];

/* ── Custom label for visited-widgets badges ─────────────────────────── */
const visitLabel = (visit: { entityRef?: string }) => {
  if (!visit.entityRef) return 'other';
  const kind = visit.entityRef.split(':')[0]?.toLowerCase();
  if (kind === 'resource') return 'page';
  return kind || 'other';
};

/* ── Quick Stats ─────────────────────────────────────────────────────── */
const STAT_ORDER = ['Backend', 'Frontend', 'Component', 'API', 'Template', 'Resource', 'System', 'Group', 'User'];

const CatalogQuickStats = () => {
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [facets, setFacets] = useState<Array<{ value: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [utgStats, setUtgStats] = useState<{ total_runs: number; total_ai_cost: number | null } | null>(null);
  const [tqStats, setTqStats] = useState<{ totalRuns: number; totalCost: number | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      catalogApi.getEntityFacets({ facets: ['kind'] }),
      catalogApi.getEntities({
        filter: { kind: 'component', 'spec.type': 'service', 'metadata.tags': 'microservices' },
        fields: ['metadata.name'],
      }),
      catalogApi.getEntities({
        filter: { kind: 'component', 'spec.type': 'website' },
        fields: ['metadata.name'],
      }),
    ]).then(([kindRes, backendRes, frontendRes]) => {
      if (!cancelled) {
        const kindFacets = kindRes.facets.kind ?? [];
        kindFacets.push(
          { value: 'Backend', count: backendRes.items.length },
          { value: 'Frontend', count: frontendRes.items.length },
        );
        setFacets(kindFacets);
        setLoading(false);
      }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [catalogApi]);

  useEffect(() => {
    discoveryApi.getBaseUrl('unit-test-generation-tracker').then(baseUrl =>
      fetchApi.fetch(`${baseUrl}/stats?days=36500`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setUtgStats(d); })
        .catch(() => {}),
    );
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    discoveryApi.getBaseUrl('test-quality-tracker').then(baseUrl =>
      fetchApi.fetch(`${baseUrl}/stats`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setTqStats(d); })
        .catch(() => {}),
    );
  }, [discoveryApi, fetchApi]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  // Sort facets by preferred order, then alphabetical for any extras
  const sorted = [...facets].sort((a, b) => {
    const ai = STAT_ORDER.indexOf(a.value);
    const bi = STAT_ORDER.indexOf(b.value);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.value.localeCompare(b.value);
  });

  const linkMap: Record<string, string> = {
    Component: '/catalog/kind/component',
    API: '/catalog?filters[kind]=api&filters[user]=all',
    Template: '/catalog/kind/template',
    Resource: '/catalog/kind/resource',
    System: '/catalog/kind/system',
    Group: '/catalog/kind/group',
    User: '/catalog/kind/user',
    Backend: '/catalog/backend',
    Frontend: '/catalog/frontend',
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        padding: '20px 24px',
      }}
    >
      {sorted.map(f => (
        <a
          key={f.value}
          href={linkMap[f.value] || `/catalog/kind/${f.value.toLowerCase()}`}
          style={{
            flex: '1 1 120px',
            minWidth: 120,
            textAlign: 'center',
            padding: '16px 12px',
            borderRadius: 8,
            border: `1px solid ${SF.border}`,
            background: SF.white,
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'box-shadow 0.2s',
          }}
        >
          <Typography
            variant="h4"
            style={{ fontWeight: 700, color: SF.navy, fontFamily: 'Gotham, sans-serif' }}
          >
            {f.count}
          </Typography>
          <Typography
            variant="body2"
            style={{ color: SF.bodyText, marginTop: 4, fontFamily: 'Gotham, sans-serif' }}
          >
            {f.value === 'Backend' || f.value === 'Frontend' ? f.value : `${f.value}s`}
          </Typography>
        </a>
      ))}
      {[
        {
          href: '/unit-test-generation',
          value: utgStats ? String(utgStats.total_runs) : '—',
          label: 'Unit Test Runs',
          color: SF.navy,
        },
        {
          href: '/test-quality',
          value: tqStats ? String(tqStats.totalRuns) : '—',
          label: 'Quality Check Runs',
          color: SF.navy,
        },
        {
          href: '/unit-test-generation',
          value: utgStats ? (utgStats.total_ai_cost !== null ? `$${Number(utgStats.total_ai_cost).toFixed(2)}` : '$0.00') : '—',
          label: 'Unit Test Cost',
          color: SF.red,
        },
        {
          href: '/test-quality',
          value: tqStats ? (tqStats.totalCost !== null ? `$${Number(tqStats.totalCost).toFixed(2)}` : '$0.00') : '—',
          label: 'Quality Check Cost',
          color: SF.red,
        },
      ].map(tile => (
        <a
          key={tile.label}
          href={tile.href}
          style={{
            flex: '1 1 120px',
            minWidth: 120,
            textAlign: 'center',
            padding: '16px 12px',
            borderRadius: 8,
            border: `1px solid ${SF.border}`,
            background: SF.white,
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'box-shadow 0.2s',
          }}
        >
          <Typography variant="h4" style={{ fontWeight: 700, color: tile.color, fontFamily: 'Gotham, sans-serif' }}>
            {tile.value}
          </Typography>
          <Typography variant="body2" style={{ color: SF.bodyText, marginTop: 4, fontFamily: 'Gotham, sans-serif' }}>
            {tile.label}
          </Typography>
        </a>
      ))}
    </div>
  );
};

/* ── Test Quality Home Card ──────────────────────────────────────────── */
type TQTrendPoint = {
  id: string;
  repo_name: string;
  quality_score: number | null;
  score_passed: boolean | null;
  model_name: string | null;
  ai_provider: string | null;
  branch_name: string | null;
  run_timestamp: string;
  workflow_run_number: number | null;
};

function tqScoreColor(score: number | string | null): string {
  if (score === null || score === undefined) return '#9E9E9E';
  const n = Number(score);
  if (n >= 8) return '#4CAF50';
  if (n >= 6) return '#8BC34A';
  if (n >= 4) return '#FF9800';
  return '#F44336';
}

function tqFormatDate(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TestQualityHomeCard = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [runs, setRuns] = useState<TQTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ totalRuns: number; avgScore: number | null; passCount: number } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('test-quality-tracker');
      const [tRes, sRes] = await Promise.all([
        fetchApi.fetch(`${baseUrl}/trends?limit=8`),
        fetchApi.fetch(`${baseUrl}/stats`),
      ]);
      if (tRes.ok) {
        const data: TQTrendPoint[] = await tRes.json();
        // show most-recent first
        setRuns([...data].reverse());
      }
      if (sRes.ok) setStats(await sRes.json());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Card header matching cardWrapper's MuiCardHeader style */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px 12px',
          borderBottom: `2px solid ${SF.red}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TrackChangesIcon style={{ fontSize: 18, color: SF.navy }} />
          <Typography
            style={{
              fontWeight: 700,
              fontSize: '1rem',
              color: SF.navy,
              fontFamily: 'Gotham, sans-serif',
            }}
          >
            Test Quality Tracker
          </Typography>
        </div>
        <a
          href="/test-quality"
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: SF.red,
            textDecoration: 'none',
          }}
        >
          View all →
        </a>
      </div>

      {/* Summary stats row */}
      {stats && (
        <div
          style={{
            display: 'flex',
            gap: 0,
            borderBottom: `1px solid ${SF.border}`,
          }}
        >
          {[
            { label: 'Total Runs', value: stats.totalRuns },
            {
              label: 'Avg Score',
              value: stats.avgScore !== null ? `${stats.avgScore}/10` : '—',
              color: tqScoreColor(stats.avgScore),
            },
            {
              label: 'Pass Rate',
              value: stats.totalRuns > 0
                ? `${((stats.passCount / stats.totalRuns) * 100).toFixed(0)}%`
                : '—',
              color: stats.totalRuns > 0 && stats.passCount / stats.totalRuns >= 0.7
                ? '#4CAF50'
                : '#F44336',
            },
          ].map(s => (
            <div
              key={s.label}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '10px 8px',
                borderRight: `1px solid ${SF.border}`,
              }}
            >
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: '1.15rem',
                  color: s.color ?? SF.navy,
                  lineHeight: 1.2,
                }}
              >
                {String(s.value)}
              </Typography>
              <Typography
                style={{
                  fontSize: '0.68rem',
                  color: SF.bodyText,
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.05em',
                  marginTop: 2,
                }}
              >
                {s.label}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {/* Run list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && !loading && (
          <Box px={2} py={2}>
            <Typography variant="body2" style={{ color: SF.red }}>
              {error}
            </Typography>
          </Box>
        )}
        {!loading && !error && runs.length === 0 && (
          <Box px={2} py={3} textAlign="center">
            <Typography variant="body2" style={{ color: SF.bodyText }}>
              No runs recorded yet.
            </Typography>
            <Typography variant="caption" style={{ color: SF.bodyText, display: 'block', marginTop: 4 }}>
              Runs appear here after the GitHub Actions workflow posts data.
            </Typography>
          </Box>
        )}
        {!loading && runs.map((r, i) => (
          <div
            key={r.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '9px 20px',
              borderBottom: i < runs.length - 1 ? `1px solid ${SF.border}` : 'none',
              gap: 10,
            }}
          >
            {/* Score badge */}
            <div
              style={{
                minWidth: 38,
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: tqScoreColor(r.quality_score),
                lineHeight: 1,
              }}
            >
              {r.quality_score !== null ? Number(r.quality_score).toFixed(1) : '—'}
            </div>

            {/* Pass / fail icon */}
            <div style={{ minWidth: 18 }}>
              {r.score_passed === true && (
                <Tooltip title="Passed">
                  <CheckCircleIcon style={{ fontSize: 15, color: '#4CAF50', display: 'block' }} />
                </Tooltip>
              )}
              {r.score_passed === false && (
                <Tooltip title="Failed">
                  <CancelIcon style={{ fontSize: 15, color: '#F44336', display: 'block' }} />
                </Tooltip>
              )}
            </div>

            {/* Repo + branch */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: SF.navy,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.repo_name.includes('/') ? r.repo_name.split('/')[1] : r.repo_name}
              </Typography>
              <Typography
                variant="caption"
                style={{
                  color: SF.bodyText,
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                }}
              >
                {r.branch_name ?? 'unknown branch'}
              </Typography>
            </div>

            {/* Model chip */}
            {r.model_name && (
              <Tooltip title={`${r.model_name}${r.ai_provider ? ` · ${r.ai_provider}` : ''}`}>
                <Chip
                  label={r.model_name.split('-')[0]}
                  size="small"
                  style={{
                    fontSize: '0.62rem',
                    height: 18,
                    maxWidth: 72,
                    backgroundColor: SF.navy,
                    color: SF.white,
                  }}
                />
              </Tooltip>
            )}

            {/* Timestamp */}
            <Typography
              variant="caption"
              style={{ color: SF.bodyText, whiteSpace: 'nowrap', fontSize: '0.7rem' }}
            >
              {tqFormatDate(r.run_timestamp)}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Unit Test Generation Stat Tiles ─────────────────────────────────── */
/* ── Component ───────────────────────────────────────────────────────── */
export const HomePageContent = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [badgeText, setBadgeText] = useState('ARC · Developer Portal');

  useEffect(() => {
    discoveryApi.getBaseUrl('portal-settings').then(baseUrl => {
      fetchApi
        .fetch(`${baseUrl}?key=hero_badge_text`)
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data?.value) setBadgeText(data.value); })
        .catch(() => {});
    });

    const handler = (e: Event) => setBadgeText((e as CustomEvent).detail);
    window.addEventListener(PORTAL_BADGE_EVENT, handler);
    return () => window.removeEventListener(PORTAL_BADGE_EVENT, handler);
  }, [discoveryApi, fetchApi]);

  return (
    <SearchContextProvider>
      <div className={classes.root}>
        {/* ── Hero ── */}
        <div className={classes.hero}>
          <div className={classes.heroDeco1} />
          <div className={classes.heroDeco2} />

          <div className={classes.heroInner}>
            <div className={classes.heroBadge}>{badgeText}</div>

            <Typography component="h1" className={classes.heroTitle}>
              Welcome to&nbsp;
              <span className={classes.heroRed}>SF</span>
              &nbsp;BackStage
            </Typography>

            <Typography className={classes.heroSubtitle}>
              Discover, manage and build software with SourceFuse&apos;s unified
              developer platform.
            </Typography>

            <div className={classes.searchWrapper}>
              <HomePageSearchBar placeholder="Search services, APIs, docs…" />
            </div>
          </div>
        </div>

        {/* ── Red → navy accent divider ── */}
        <div className={classes.accentStrip} />

        {/* ── Content grid ── */}
        <div className={classes.mainContent}>
          <Grid container spacing={3}>
            {/* ── Quick Stats ── */}
            <Grid item xs={12}>
              <div className={classes.cardWrapper}>
                <CatalogQuickStats />
              </div>
            </Grid>

            <VisitDisplayProvider getLabel={visitLabel}>
              <Grid item xs={12} md={6}>
                <div className={classes.cardWrapper}>
                  <HomePageTopVisited />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.cardWrapper}>
                  <HomePageRecentlyVisited />
                </div>
              </Grid>
            </VisitDisplayProvider>

            <Grid item xs={12} md={6}>
              <div className={`${classes.cardWrapper} starred-entities-wrapper`}>
                <HomePageStarredEntities />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.cardWrapper}>
                <HomePageToolkit tools={toolkitTools} />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={`${classes.cardWrapper} announcements-home-wrapper`}>
                <AnnouncementErrorBoundary>
                  <AnnouncementsCard max={3} />
                </AnnouncementErrorBoundary>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.cardWrapper}>
                <TestQualityHomeCard />
              </div>
            </Grid>



          </Grid>
        </div>
      </div>
    </SearchContextProvider>
  );
};
